import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabase-client";

export default function Muestras() {
   const [muestra, setMuestra] = useState([]);
   const [tasks, setTasks] = useState([]);
   const [selected, setSelected] = useState("");
   const [isLoading, setIsLoading] = useState(true);
   const [obraActiva, setObraActiva] = useState(null);
   const [imagenAmpliada, setImagenAmpliada] = useState(null);
   const [modalImageIndex, setModalImageIndex] = useState(0);
   const [imageMeta, setImageMeta] = useState({});

   const fetchTabla = async () => {
      const { error, data } = await supabase
         .from("tabla")
         .select("*")
         .eq("cod", 7)
         .gt("orden", 1)
         .order("created_at", { ascending: true });

      if (error) {
         console.error("Error reading task: ", error.message);
         return;
      }

      setTasks(data);
   };

   const fetchMuestra = async () => {
      setIsLoading(true);
      const { error, data } = await supabase
         .from("muestra")
         .select(
            `
         id,titulo,descripcion,filtros,estado,
        muestraimagenes(texto,orden,imagen_url)
        `,
         )
         .eq("estado", 2)
         .eq("muestraimagenes.portada", 1)
         .neq("filtros", '["6"]') // Exclude obras with filtro "6"
         .order("titulo", { ascending: true });

      if (error) {
         console.error("Error reading muestra: ", error.message);
         setIsLoading(false);
         return;
      }

      setMuestra(data || []);
      setIsLoading(false);
   };

   useEffect(() => {
      fetchTabla();
      fetchMuestra();
   }, []);

   useEffect(() => {
      const handleEsc = (event) => {
         if (event.key === "Escape") {
            if (imagenAmpliada) {
               setImagenAmpliada(null);
               return;
            }
            setObraActiva(null);
         }
      };

      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
   }, [imagenAmpliada]);

   const filterMuestras = (muestraList, selectedValue) => {
      const filtrosArr = [];
      if (selectedValue) {
         for (let i = 0; i < muestraList.length; i++) {
            if (muestraList[i].filtros && muestraList[i].filtros.includes('"' + selectedValue + '"')) {
               filtrosArr.push(muestraList[i]);
            }
         }
         return filtrosArr;
      } else {
         return muestraList;
      }
   };

   const handleFilterChange = (e) => {
      const value = e.target.value;
      setSelected(value);
   };

   const obrasFiltradas = useMemo(() => {
      return filterMuestras(muestra, selected);
   }, [selected, muestra]);

   const handleOpenObra = async (obra) => {
      setModalImageIndex(0);

      const { data, error } = await supabase
         .from("muestraimagenes")
         .select("texto,orden,imagen_url,portada")
         .eq("obra_id", obra.id)
         .order("orden", { ascending: true });

      if (error) {
         console.error("Error reading obra images: ", error.message);
         setObraActiva(obra);
         return;
      }

      setObraActiva({
         ...obra,
         muestraimagenes: data || [],
      });
   };

   const handleCloseObra = () => {
      setImagenAmpliada(null);
      setObraActiva(null);
   };

   const saveImageMeta = (key, event) => {
      const { naturalWidth, naturalHeight } = event.currentTarget;
      setImageMeta((prev) => {
         if (prev[key]?.naturalWidth === naturalWidth && prev[key]?.naturalHeight === naturalHeight) {
            return prev;
         }
         return {
            ...prev,
            [key]: { naturalWidth, naturalHeight },
         };
      });
   };

   const getGalleryFrameClass = (key) => {
      const meta = imageMeta[key];
      if (!meta) {
         return "h-64 sm:h-72 lg:h-80";
      }

      return meta.naturalHeight > meta.naturalWidth
         ? "h-80 sm:h-[26rem] lg:h-[30rem]"
         : "h-56 sm:h-64 lg:h-72";
   };

   const getModalFrameClass = (key) => {
      const meta = imageMeta[key];
      if (!meta) {
         return "h-72 sm:h-96 md:h-full md:min-h-[30rem]";
      }

      return meta.naturalHeight > meta.naturalWidth
         ? "h-[26rem] sm:h-[34rem] md:h-full md:min-h-[40rem]"
         : "h-72 sm:h-96 md:h-full md:min-h-[28rem]";
   };

   return (
      <main className="min-h-screen bg-[#FAFAF8]">
         {/* Header */}
         <div className="border-b border-[#EDE9E4] py-16 px-6 text-center">
            <p className="text-[0.68rem] uppercase tracking-[0.25em] text-[#9B6D3F] mb-4">Colección Sagrada</p>
            <h1
               className="text-5xl md:text-6xl font-medium text-[#1A1A1A]"
               style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
               Arte Sacro
            </h1>
            <p className="mt-5 max-w-xl mx-auto text-[0.9rem] text-[#7A7570] leading-relaxed">
               Piezas únicas, talladas a mano. Muchas de estas obras se encuentran en capillas, catedrales y colecciones privadas. Realizadas por encargo.
            </p>
            <div className="w-10 h-[1px] bg-[#9B6D3F] mx-auto mt-7" />
         </div>

         {/* Filter */}
         <div className="max-w-7xl mx-auto px-6 pt-10 pb-0">
            <div className="flex items-center gap-4">
               <label className="text-[0.68rem] uppercase tracking-[0.18em] text-[#7A7570]">Filtrar por tipo</label>
               <select
                  value={selected}
                  onChange={handleFilterChange}
                  className="appearance-none bg-transparent border-b border-[#9B6D3F] px-2 py-1.5 text-sm text-[#1A1A1A] focus:outline-none cursor-pointer"
               >
                  <option value="">Ver todos</option>
                  {tasks.map((item) => (
                     <option key={item.id} value={item.id}>{item.descripcion}</option>
                  ))}
               </select>
            </div>
         </div>

         {/* Gallery Grid */}
         <section className="max-w-7xl mx-auto px-6 py-12">
            {isLoading ? (
               <div className="flex justify-center py-20">
                  <div className="w-8 h-8 border-2 border-[#9B6D3F] border-t-transparent rounded-full animate-spin" />
               </div>
            ) : obrasFiltradas.length === 0 ? (
               <div className="text-center py-20">
                  <p className="text-[0.78rem] uppercase tracking-[0.12em] text-[#7A7570]">No hay obras para el tipo seleccionado.</p>
               </div>
            ) : (
               <div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-3">
                  {obrasFiltradas.map((obra) => {
                     const imagen = obra.muestraimagenes?.[0]?.imagen_url || "";
                     const imageKey = `${obra.id}-${imagen}`;
                     const titulo = obra?.titulo || "Obra sin título";

                     return (
                        <article
                           key={obra.id}
                           className="group cursor-pointer"
                           onClick={() => handleOpenObra(obra)}
                        >
                           <div className={`overflow-hidden bg-[#F5F0EB] ${getGalleryFrameClass(imageKey)}`}>
                              {imagen ? (
                                 <img
                                    src={imagen}
                                    alt={titulo}
                                    onLoad={(e) => saveImageMeta(imageKey, e)}
                                    className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
                                 />
                              ) : (
                                 <div className="flex items-center justify-center h-full text-sm text-[#9B6D3F]">Sin imagen</div>
                              )}
                           </div>
                           <div className="pt-4 pb-1">
                              <h2
                                 className="text-lg font-medium text-[#1A1A1A]"
                                 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                              >
                                 {titulo}
                              </h2>
                              <p className="text-[0.72rem] uppercase tracking-[0.1em] text-[#9B6D3F] mt-1">Ver obra →</p>
                           </div>
                        </article>
                     );
                  })}
               </div>
            )}
         </section>

         {/* Detail Modal */}
         {obraActiva && (() => {
            const fotos = obraActiva?.muestraimagenes || [];
            const fotoActiva = fotos[modalImageIndex];
            const activeKey = `${obraActiva.id}-${fotoActiva?.imagen_url}`;

            return (
               <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                  onClick={handleCloseObra}
               >
                  <div
                     className="max-h-[90vh] w-full max-w-4xl overflow-auto bg-white shadow-2xl"
                     onClick={(e) => e.stopPropagation()}
                  >
                     {/* Modal Header */}
                     <div className="flex items-center justify-between border-b border-[#EDE9E4] px-6 py-4 bg-white sticky top-0 z-10">
                        <h3
                           className="text-xl font-medium text-[#1A1A1A]"
                           style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                        >
                           {obraActiva?.titulo || "Detalle"}
                        </h3>
                        <button
                           onClick={handleCloseObra}
                           className="text-[0.7rem] uppercase tracking-[0.12em] text-[#7A7570] hover:text-[#1A1A1A] transition-colors px-3 py-1 border border-[#EDE9E4] hover:border-[#1A1A1A]"
                        >
                           Cerrar ✕
                        </button>
                     </div>

                     {/* Modal Body */}
                     <div className="grid gap-0 md:grid-cols-2">
                        {/* Image Side */}
                        <div className={`flex items-center justify-center p-6 bg-[#F5F0EB] ${getModalFrameClass(activeKey)}`}>
                           {fotoActiva?.imagen_url ? (
                              <button
                                 onClick={() => setImagenAmpliada(fotoActiva)}
                                 className="cursor-zoom-in h-full w-full"
                                 type="button"
                              >
                                 <img
                                    src={fotoActiva.imagen_url}
                                    alt={fotoActiva.texto || obraActiva?.titulo}
                                    onLoad={(e) => saveImageMeta(activeKey, e)}
                                    className="h-full w-full object-contain"
                                 />
                              </button>
                           ) : (
                              <div className="text-sm text-[#9B6D3F]">Sin imagen</div>
                           )}
                        </div>

                        {/* Info Side */}
                        <div className="p-6 space-y-5">
                           <h2
                              className="text-2xl font-medium text-[#1A1A1A] leading-tight"
                              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                           >
                              {obraActiva?.titulo || "Obra sin título"}
                           </h2>

                           {obraActiva?.descripcion !== "-" && obraActiva?.descripcion && (
                              <p className="text-sm leading-relaxed text-[#7A7570]">
                                 {obraActiva.descripcion}
                              </p>
                           )}

                           {/* Thumbnails */}
                           {fotos.length > 1 && (
                              <div className="flex gap-2 overflow-x-auto pb-1">
                                 {fotos.map((foto, idx) => (
                                    <button
                                       key={idx}
                                       type="button"
                                       onClick={(e) => { e.stopPropagation(); setModalImageIndex(idx); }}
                                       className={`flex-shrink-0 w-14 h-14 overflow-hidden transition-all ${
                                          idx === modalImageIndex
                                             ? "ring-2 ring-[#9B6D3F]"
                                             : "opacity-50 hover:opacity-80"
                                       }`}
                                    >
                                       <img src={foto.imagen_url} alt={foto.texto || `Foto ${idx + 1}`} className="w-full h-full object-cover" />
                                    </button>
                                 ))}
                              </div>
                           )}

                           {fotoActiva?.texto && fotoActiva.texto !== "-" && (
                              <p className="text-xs italic text-[#9B6D3F]">{fotoActiva.texto}</p>
                           )}

                           <div className="pt-4 border-t border-[#EDE9E4]">
                              <p className="text-sm text-[#7A7570]">
                                 ¿Le interesa esta obra?{" "}
                                 <a href="/contacto" className="text-[#9B6D3F] underline underline-offset-2 hover:text-[#6B4A2A]">
                                    Contáctenos
                                 </a>
                              </p>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            );
         })()}

         {/* Full-size Lightbox */}
         {imagenAmpliada?.imagen_url && (
            <div
               className="fixed inset-0 z-[60] flex items-center justify-center bg-black/92 p-4"
               onClick={() => setImagenAmpliada(null)}
            >
               <div
                  className="relative max-h-[95vh] w-full max-w-7xl flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
               >
                  <button
                     onClick={() => setImagenAmpliada(null)}
                     className="absolute right-0 top-0 text-white/60 hover:text-white text-[0.72rem] uppercase tracking-[0.12em] bg-white/10 hover:bg-white/20 px-4 py-2 transition-colors"
                     type="button"
                  >
                     Cerrar ✕
                  </button>
                  <img
                     src={imagenAmpliada.imagen_url}
                     alt={imagenAmpliada.texto || obraActiva?.titulo || "Obra"}
                     className="max-h-[92vh] w-auto max-w-full object-contain"
                  />
               </div>
            </div>
         )}
      </main>
   );
}
